import {Component, Inject, OnInit, DestroyRef, OnDestroy, NgZone, DOCUMENT} from '@angular/core';
import {NgClass} from '@angular/common';
import {NgEventBus} from 'ng-event-bus';
import {filter, switchMap, tap} from 'rxjs/operators';
import {RibbonService} from '../services/ribbon.service';
import {AppConfigService} from '../../common/service/app-config.service';
import {MapService} from '../services/map.service';
import {HttpClient} from '@angular/common/http';
import {SpatialSnapshotService} from '../snapshot/spatial-snapshot.service';
import {WidgetService} from '../services/widget.service';
import {MapToolsService} from '../services/map-tools.service';
import {MapModeService} from '../services/map-mode.service';
import {AttributeViewerService} from '../widgets/attribute-viewer/attribute-viewer.service';
import {AlertModalService, ConfirmationModalService, SettingsModalService} from '../../common/component/modal';
import {GraphicSelectionService} from '../services/graphic-selection.service';
import {SpatialUtilService} from '../services/spatial-util.service';
import {PermissionsService} from '../../common/service/permissions.service';
import {PlanModelService} from '../../common/service/plan-model.service';
import {auditTime, distinctUntilChanged, EMPTY, fromEvent, map, merge, Observable, of, Subscription} from 'rxjs';
import {NlrPermissionsDisabledPipe} from '../../common/pipe/nlr-permissions-disabled-pipe';
import {DisplayPreferencesComponent} from '../widgets/display-preferences/display-preferences.component';
import {VisibilitySettingsComponent} from '../widgets/visibility-settings/visibility-settings.component';
import {DisplayModeComponent} from '../widgets/display-mode/display-mode.component';
import {SpatialSnapshotsComponent} from '../widgets/snapshot/spatial-snapshots.component';
import {RemovalsComponent} from '../removals/removals.component';
import {ZoomScaleComponent} from '../widgets/zoom-scale/zoom-scale.component';
import {ScaleFeaturesComponent} from '../widgets/scale-features/scale-features.component';
import {MoveAnnotationComponent} from '../widgets/move-annotation/move-annotation.component';
import {RotateAnnotationComponent} from '../widgets/rotate-annotation/rotate-annotation.component';
import {AnnotationComponent} from '../widgets/annotation/annotation.component';
import {AnnotationToolsComponent} from '../widgets/annotation-tools/annotation-tools.component';
import {ConstructionToolsComponent} from '../widgets/construction-tools/construction-tools.component';
import {SelectionMethodsComponent} from '../widgets/selection-methods/selection-methods.component';
import {PrintAreaComponent} from '../widgets/print-area/print-area.component';
import {AttributeViewerComponent} from '../widgets/attribute-viewer/attribute-viewer.component';
import {EnlargementComponent} from '../widgets/enlargement/enlargement.component';
import {ExportSurveyDataComponent} from '../widgets/export-survey-data/export-survey-data.component';
import {ImportSurveyDataComponent} from '../widgets/import-survey-data/import-survey-data.component';
import {MapModeComponent} from '../widgets/map-mode/map-mode.component';
import {IdentifyFeaturesComponent} from '../widgets/identify-features/identify-features.component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable  @typescript-eslint/no-unused-vars */
@Component({
    selector: 'nlr-ribbon',
    templateUrl: './ribbon.component.html',
    imports: [
        NgClass,
        NlrPermissionsDisabledPipe,
        DisplayPreferencesComponent,
        VisibilitySettingsComponent,
        DisplayModeComponent,
        SpatialSnapshotsComponent,
        RemovalsComponent,
        ZoomScaleComponent,
        ScaleFeaturesComponent,
        MoveAnnotationComponent,
        RotateAnnotationComponent,
        AnnotationComponent,
        AnnotationToolsComponent,
        ConstructionToolsComponent,
        SelectionMethodsComponent,
        PrintAreaComponent,
        AttributeViewerComponent,
        EnlargementComponent,
        ExportSurveyDataComponent,
        ImportSurveyDataComponent,
        MapModeComponent,
        IdentifyFeaturesComponent
    ],
    styleUrl: './ribbon.component.scss'
})
export class RibbonComponent implements OnInit, OnDestroy {
    ctrlObservable: Observable<boolean>;
    shiftObservable: Observable<boolean>;
    private sub: Subscription;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private ribbonService: RibbonService,
        private eventBus: NgEventBus,
        private appConfigService: AppConfigService,
        private mapService: MapService,
        private http: HttpClient,
        private spatialSnapshotService: SpatialSnapshotService,
        private widgetService: WidgetService,
        private mapToolsService: MapToolsService,
        private mapModeService: MapModeService,
        private attributeViewerService: AttributeViewerService,
        private settingsModalService: SettingsModalService,
        private graphicSelectionService: GraphicSelectionService,
        private spatialUtilService: SpatialUtilService,
        private alertModalService: AlertModalService,
        private confirmationModalService: ConfirmationModalService,
        private permissionsService: PermissionsService,
        private planModelService: PlanModelService,
        private destroyRef: DestroyRef,
        private ngZone: NgZone
    ) {}

    ngOnInit() {
        this.ngZone.runOutsideAngular(() => {
            this.ctrlObservable = merge(
                fromEvent<KeyboardEvent>(document, 'keydown').pipe(
                    filter(e => e.key === 'Control'),
                    map(() => true)
                ),
                fromEvent<KeyboardEvent>(document, 'keyup').pipe(
                    filter(e => e.key === 'Control'),
                    map(() => false)
                )
            ).pipe(distinctUntilChanged());

            this.shiftObservable = merge(
                fromEvent<KeyboardEvent>(document, 'keydown').pipe(
                    filter(e => e.key === 'Shift'),
                    map(() => true)
                ),
                fromEvent<KeyboardEvent>(document, 'keyup').pipe(
                    filter(e => e.key === 'Shift'),
                    map(() => false)
                )
            ).pipe(distinctUntilChanged());

            // Optional: trigger the streams so they stay alive
            this.sub = merge(this.ctrlObservable, this.shiftObservable).subscribe();
        });
        this.eventBus
            .on('featureHighlightsChanged')
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                auditTime(50),
                filter(() => !this.widgetService.isWidgetActive('Enlargement') && this.mapToolsService.isActive('IDENTIFY_FEATURES')),
                tap(() => {
                    console.debug('on featureHighlightChanged  ribbon.service');
                })
            )
            .subscribe(meta => {
                const oldFeatureHighlightsMap = meta.data;
                this.ngZone.runOutsideAngular(() => {
                    const newHighlightsCount = this.spatialUtilService.getGeometryFeatureHighlightsCount();

                    if (!this.widgetService.isWidgetActive('IdentifyFeatures')) {
                        if (newHighlightsCount > 1) {
                            this.widgetService.onClick('IdentifyFeatures');
                        }
                    } else if (
                        this.isCtrlKeyDown() ||
                        this.isShiftKeyDown() ||
                        this.spatialUtilService
                            .getGeometryFeatureClasses()
                            .some(k => this.spatialUtilService.getFeatureHighlightsMap()[k].length > oldFeatureHighlightsMap[k].length)
                    ) {
                        this.eventBus.cast('resetFeaturesInteracted');
                    }
                });
            });
        this.init();
    }

    ngOnDestroy(): void {
        this.sub?.unsubscribe();
    }

    title = 'RibbonController';
    editViewDesignPermissions = ['EDIT_DESIGN', 'VIEW_DESIGN'];
    featuresInteracted = false;
    disableAllWidgets = false;

    widgetStyle;
    trustedWidgetStyle;

    ctrlKeyDown = false;
    shiftKeyDown = false;

    isCtrlKeyDown(): boolean {
        return this.ctrlKeyDown;
    }

    isShiftKeyDown(): boolean {
        return this.shiftKeyDown;
    }

    getWidgetStyle() {
        const newWidgetStyle = this.widgetService.getWidgetStyle();
        if (newWidgetStyle === this.widgetStyle) {
            return this.trustedWidgetStyle;
        }
        this.widgetStyle = newWidgetStyle;
        ///TODO ?????
        ///this.trustedWidgetStyle = $sce.trustAsCss(widgetStyle);
        return this.trustedWidgetStyle;
    }

    isMapModeMainPlan() {
        return this.mapModeService.mapMode.type === 'mainPlan';
    }

    isWidgetActive(widget) {
        return this.widgetService.isWidgetActive(widget);
    }

    isWidgetSuspended(widget) {
        return this.widgetService.isWidgetSuspended(widget);
    }

    isWidgetDisabled(widget?) {
        return this.widgetService.isWidgetDisabled(widget, this.mapModeService.mapMode);
    }

    unscaledFeatures() {
        return !this.mapToolsService.isScaleActive();
    }

    validateDisabled() {
        return this.planModelService.readonly || this.isWidgetDisabled();
    }

    validate() {
        console.info(`${this.title} : validate`);

        this.mapToolsService.activate('VALIDATE').subscribe();
    }

    onClick(widget) {
        this.widgetService.onClick(widget);
    }

    alignFlipAnnotationDisabled() {
        return this.planModelService.readonly || this.unscaledFeatures() || this.isPrintPreview();
    }

    annotationToolsDisabled() {
        return this.planModelService.readonly;
    }

    attributeViewerDisabled() {
        return this.isWidgetDisabled('AttributeViewer');
    }

    clearSelectedFeaturesDisabled() {
        return false;
    }

    constructionToolsRibbonButtonDisabled() {
        return this.planModelService.readonly || this.permissionsService.isDisabledByPermissions(this.editViewDesignPermissions);
    }

    cadastreToolsRibbonButtonDisabled() {
        return this.showHideCadastreDisabled() || this.permissionsService.isDisabledByPermissions(this.editViewDesignPermissions);
    }

    displayModeDisabled() {
        return this.isWidgetDisabled('DisplayMode');
    }

    enlargementDisabled() {
        return this.planModelService.readonly;
    }

    exportDisabled() {
        return this.isWidgetDisabled('ExportSurveyData');
    }

    identifyFeaturesDisabled() {
        return false;
    }

    moveAnnotationDisabled() {
        return this.planModelService.readonly || this.unscaledFeatures() || this.isPrintPreview();
    }

    printAreaDisabled() {
        return (
            this.planModelService.readonly ||
            this.isWidgetDisabled('PrintArea') ||
            this.isPrintPreview() ||
            !this.isMapModeMainPlan() ||
            this.permissionsService.isDisabledByPermissions(this.editViewDesignPermissions)
        );
    }

    printPlanDisabled() {
        return this.isWidgetDisabled();
    }

    removalsDisabled() {
        return this.planModelService.readonly || this.isPrintPreview();
    }

    rotateAnnotationDisabled() {
        return this.planModelService.readonly || this.unscaledFeatures() || this.isPrintPreview();
    }

    selectAllFeaturesDisabled() {
        return false;
    }

    selectionMethodsDisabled() {
        return false;
    }

    showHideCadastreDisabled() {
        return this.planModelService.readonly || this.isPrintPreview();
    }

    showFeaturesDisabled() {
        return (
            this.planModelService.readonly || this.isPrintPreview() || this.permissionsService.isDisabledByPermissions(this.editViewDesignPermissions)
        );
    }

    snapshotsDisabled() {
        return this.planModelService.readonly || this.isPrintPreview();
    }

    hideFeaturesDisabled() {
        return (
            this.planModelService.readonly || this.isPrintPreview() || this.permissionsService.isDisabledByPermissions(this.editViewDesignPermissions)
        );
    }

    visibilitySettingsDisabled() {
        return this.isPrintPreview();
    }

    onClickExport() {
        (this.spatialUtilService.hasEdits()
            ? this.confirmationModalService
                  .showModal({
                      title: 'Save plan?',
                      confirmationText: 'The plan must be saved before it can be exported.<br>Do you want to save the plan?',
                      iconType: 'info'
                  })
                  .pipe(
                      tap((action: string) => {
                          if (action === 'yes') {
                              this.saveFeatures();
                          }
                      }),
                      takeUntilDestroyed(this.destroyRef)
                  )
            : this.openExportSurveyData()
        ).subscribe();
    }

    openExportSurveyData() {
        this.widgetService.onClick('ExportSurveyData');
        return EMPTY;
    }

    saveFeatures() {
        const subscription = this.spatialUtilService.save().subscribe({
            complete: () => {
                this.widgetService.onClick('ExportSurveyData');
            }
        });
        this.eventBus.cast('nlr:busy', subscription);
    }

    onClickShowHideCadastre() {
        this.mapToolsService.toggle('SHOW_HIDE_CADASTRE');
    }

    isShowHideCadastreActive() {
        return this.mapToolsService.isActive('SHOW_HIDE_CADASTRE');
    }

    selectAllFeatures() {
        this.mapToolsService.activate('SELECT_ALL_FEATURES').subscribe();
    }

    clearSelectedFeatures() {
        console.debug(this.title + ' : clearSelectedFeatures');
        this.mapToolsService.activate('DESELECT_ALL_FEATURES').subscribe();
    }

    printToPdf() {
        console.debug(this.title + ' : printToPdf');
        this.mapToolsService.activate('PRINT_PLAN_WINDOW').subscribe();
    }

    toggleLockAutoPan() {
        this.mapToolsService.toggle('LOCK_AUTO_PAN');
    }

    lockAutoPanDisabled() {
        return false;
    }

    lockAutoPanActive() {
        return this.mapToolsService.isActive('LOCK_AUTO_PAN');
    }

    toggleIdentifyFeatures() {
        if (this.mapToolsService.isActive('IDENTIFY_FEATURES')) {
            this.mapToolsService.deactivate('IDENTIFY_FEATURES');
        } else {
            this.mapToolsService.activate('IDENTIFY_FEATURES').subscribe();
        }
    }

    isIdentifyFeaturesActive() {
        return this.mapToolsService.isActive('IDENTIFY_FEATURES');
    }

    showOrHideWidgetContent(widgetName, widgetOpen) {
        this.widgetService.showOrHideWidgetContent(widgetName, widgetOpen);
    }

    isPrintPreview() {
        return this.mapModeService.viewMode === this.appConfigService.getDesignProperties().VIEW_MODES.PRINT_PREVIEW_MODE;
    }

    getHelpUrl(url, title) {
        return `/components/help/help.html?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`;
    }

    showFeatures(visible) {
        console.debug(`${this.title} - showFeatures:`, visible);

        this.mapToolsService.activate(visible ? 'SHOW_FEATURES' : 'HIDE_FEATURES').subscribe();
    }

    isScaleAnnotationToolActive() {
        return this.mapToolsService.isScaleActive();
    }

    importDisabled() {
        return (
            this.planModelService.readonly ||
            this.isWidgetDisabled('ImportSurveyData') ||
            this.permissionsService.isDisabledByPermissions(this.editViewDesignPermissions)
        );
    }

    saveDisabled() {
        return this.planModelService.readonly || !this.spatialUtilService.hasEdits();
    }

    save() {
        this.mapToolsService.activate('SAVE').subscribe();
    }

    getTooltip(tool, tooltip) {
        return this.mapToolsService.getTooltipWithShortcut(tool, tooltip);
    }

    //Pre-load templates
    // [
    //     'components/design/widgets/display-mode/display-mode.html',
    //     'components/design/widgets/display-preferences/display-preferences.html',
    //     'components/design/widgets/visibility-settings/visibility-settings.html'
    // ].forEach(t => $http.get(t).then(res => $templateCache.put(t, res.data)));
    init() {
        this.mapToolsService.init(
            'ANNOTATION_TOOLS_WINDOW',
            () => {},
            () => {},
            () => !this.permissionsService.hasEditPermission(this.editViewDesignPermissions) || this.planModelService.readonly
        );

        this.mapToolsService.init(
            'ATTRIBUTE_VIEWER_WINDOW',
            () => {},
            () => {
                if (this.mapToolsService.isActive('ADD_ADDITIONAL_ANNOTATION')) {
                    this.mapToolsService.deactivate('ADD_ADDITIONAL_ANNOTATION');
                }
            },
            () => this.attributeViewerDisabled()
        );

        //TODO replace with snippet below when nlrpWidgetWindowManager is avaliable
        this.mapToolsService.init(
            'CONSTRUCTION_TOOLS_WINDOW',
            () => {},
            suspend => !suspend,
            () => !this.permissionsService.hasEditPermission(this.editViewDesignPermissions) || this.planModelService.readonly
        );

        // this.mapToolsService.init(
        //       'CONSTRUCTION_TOOLS_WINDOW',
        //       () => {},
        //       suspend => !suspend && nlrpWidgetWindowManager.close('CONSTRUCTION_TOOLS_WINDOW'),
        //       () => !this.permissionsService.hasEditPermission(this.editViewDesignPermissions) || this.planModelService.readonly
        //   );

        this.mapToolsService.init(
            'DISPLAY_MODE_WINDOW',
            () => this.adjustVisibilitySettings('button[name=displayMode]', '#displayMode', true),
            () => {},
            () => this.displayModeDisabled()
        );

        this.mapToolsService.init(
            'DISPLAY_PREFERENCES_WINDOW',
            () => this.adjustVisibilitySettings('button[name=displayPreferences]', '#displayPreferences', false),
            () => {}
        );

        this.mapToolsService.init(
            'ENLARGEMENT_WINDOW',
            () => {},
            () => this.mapToolsService.deactivate('ENLARGEMENT'),
            () => !this.permissionsService.hasEditPermission(this.editViewDesignPermissions) || this.enlargementDisabled()
        );

        this.mapToolsService.init(
            'EXPORT_WINDOW',
            () => {},
            () => {},
            () => this.exportDisabled()
        );

        this.mapToolsService.init(
            'IMPORT_WINDOW',
            () => {},
            () => {},
            () => !this.permissionsService.hasEditPermission(this.editViewDesignPermissions) || this.importDisabled()
        );

        this.mapToolsService.init(
            'IDENTIFY_FEATURES_WINDOW',
            () => {},
            () => {}
        );

        // NOTE: The IDENTIFY_FEATURES tool will remain active when the IDENTIFY_FEATURES_WINDOW is closed
        this.mapToolsService.init(
            'IDENTIFY_FEATURES',
            () => {},
            () => {
                if (this.mapToolsService.isActive('IDENTIFY_FEATURES_WINDOW')) {
                    this.mapToolsService.deactivate('IDENTIFY_FEATURES_WINDOW');
                }
            },
            () => this.identifyFeaturesDisabled()
        );

        this.mapToolsService
            .activate(
                'LOCK_AUTO_PAN',
                () => {},
                () => {},
                () => this.lockAutoPanDisabled()
            )
            .subscribe();

        this.mapToolsService.init(
            'PRINT_AREA_WINDOW',
            () => {},
            () => {},
            () => !this.permissionsService.hasEditPermission(this.editViewDesignPermissions) || this.printAreaDisabled()
        );

        this.mapToolsService.init(
            'PRINT_PLAN_WINDOW',
            () => {
                if (!this.isWidgetDisabled()) {
                    this.ribbonService.printToPdf();
                }
            },
            null,
            () => this.printPlanDisabled()
        );

        this.mapToolsService.init(
            'SAVE',
            () => {
                if (!this.permissionsService.hasEditPermission(this.editViewDesignPermissions)) {
                    return of(null);
                }
                return this.spatialUtilService.save().subscribe({
                    error: () => {
                        this.alertModalService.createAlertMessage(
                            'There was a problem saving spatial data. Please try again. Contact Landgate if the problem persists.',
                            'Ok',
                            'Error'
                        );
                    }
                });
            },
            null,
            () => this.saveDisabled()
        );

        this.mapToolsService.init(
            'SELECT_ALL_FEATURES',
            () => this.ribbonService.selectAllFeatures(),
            null,
            () => this.selectAllFeaturesDisabled()
        );

        this.mapToolsService.init(
            'DESELECT_ALL_FEATURES',
            () => this.ribbonService.clearSelectedFeatures(),
            null,
            () => this.clearSelectedFeaturesDisabled()
        );

        this.mapToolsService.init(
            'SHOW_FEATURES',
            () => this.ribbonService.showFeatures(true),
            null,
            () => !this.permissionsService.hasPermission(this.editViewDesignPermissions) || this.showFeaturesDisabled()
        );

        this.mapToolsService.init(
            'HIDE_FEATURES',
            () => this.ribbonService.showFeatures(false),
            null,
            () => !this.permissionsService.hasPermission(this.editViewDesignPermissions) || this.hideFeaturesDisabled()
        );

        this.mapToolsService.init(
            'SHOW_HIDE_CADASTRE',
            () => this.eventBus.cast('switchCadastre', true),
            () => this.eventBus.cast('switchCadastre', false),
            () => !this.permissionsService.hasEditPermission(this.editViewDesignPermissions) || this.showHideCadastreDisabled()
        );

        this.mapToolsService.init(
            'VALIDATE',
            () => this.savePlan(),
            null,
            () => this.validateDisabled()
        );

        this.mapToolsService.init(
            'VISIBILITY_SETTINGS_WINDOW',
            () => this.adjustVisibilitySettings('button[name=visibilitySettings]', '#visibilitySettings', true),
            () => {},
            () => this.visibilitySettingsDisabled()
        );
    }

    savePlan() {
        let observable: Observable<any>;

        if (this.spatialUtilService.hasEdits()) {
            observable = this.confirmationModalService
                .showModal({
                    title: 'Save plan?',
                    confirmationText: 'The plan must be saved before spatial rules can be run.<br>Do you want to save the plan?'
                })
                .pipe(
                    switchMap(action => {
                        if (action === 'yes') {
                            return this.spatialUtilService.save();
                        }
                        //should return nothing to stop processing
                        return of();
                    })
                );
        } else {
            observable = of(null);
        }

        return observable.subscribe(() => this.eventBus.cast('validate'));
    }

    adjustVisibilitySettings(btnQuery: string, divQuery: string, alignRight: boolean): void {
        setTimeout(() => {
            const btn = this.document.querySelector(btnQuery) as HTMLElement;
            const div = this.document.querySelector(divQuery) as HTMLElement;

            if (btn && div) {
                const left = btn.offsetLeft + (alignRight ? btn.offsetWidth - div.offsetWidth : 0);

                div.style.left = `${left}px`;
                div.style.visibility = 'visible';
            }
        }, 100);
    }
}
